// [NEW FILE] PM-4: read the first sheet of an .xlsx file, or a .csv file, into a list of row objects
// ({ header: value }). No extra library: an .xlsx file is a ZIP of XML files, unpacked with the browser's
// DecompressionStream and read with DOMParser. Dates typed as Excel dates come back as YYYY-MM-DD.
import { parseCsv } from "./exportCsv";

const u16 = (b, o) => b[o] | (b[o + 1] << 8);
const u32 = (b, o) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;

async function unzip(buffer) {
  const bytes = new Uint8Array(buffer);
  let end = bytes.length - 22;
  while (end >= 0 && u32(bytes, end) !== 0x06054b50) end -= 1; // end of central directory
  if (end < 0) throw new Error("This is not a valid Excel (.xlsx) file.");
  const count = u16(bytes, end + 10);
  let p = u32(bytes, end + 16);
  const files = {};
  for (let i = 0; i < count; i += 1) {
    const method = u16(bytes, p + 10);
    const size = u32(bytes, p + 20);
    const nameLen = u16(bytes, p + 28);
    const extraLen = u16(bytes, p + 30);
    const commentLen = u16(bytes, p + 32);
    const local = u32(bytes, p + 42);
    const name = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen));
    const dataStart = local + 30 + u16(bytes, local + 26) + u16(bytes, local + 28);
    files[name] = { method, data: bytes.subarray(dataStart, dataStart + size) };
    p += 46 + nameLen + extraLen + commentLen;
  }
  return files;
}

async function text(entry) {
  if (!entry) return "";
  if (entry.method === 0) return new TextDecoder().decode(entry.data);
  if (typeof DecompressionStream === "undefined") throw new Error("This browser cannot read .xlsx files. Save the sheet as CSV and import that.");
  const stream = new Blob([entry.data]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(stream).text();
}

const colIndex = (ref) => {
  const letters = String(ref).replace(/[0-9]/g, "");
  return [...letters].reduce((n, ch) => n * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
};

const excelDate = (serial) => {
  const ms = Math.round((Number(serial) - 25569) * 86400 * 1000);
  return new Date(ms).toISOString().slice(0, 10);
};

async function readXlsx(buffer) {
  const files = await unzip(buffer);
  const xml = (s) => new DOMParser().parseFromString(s, "application/xml");
  const shared = [...xml(await text(files["xl/sharedStrings.xml"])).getElementsByTagName("si")].map((si) => si.textContent || "");
  const sheetName = Object.keys(files).filter((n) => /^xl\/worksheets\/sheet\d+\.xml$/.test(n)).sort()[0];
  if (!sheetName) throw new Error("The Excel file has no sheet.");
  // Cells with a date number format: find the style indexes that use a date format.
  const styles = xml(await text(files["xl/styles.xml"]));
  const dateFmts = new Set([14, 15, 16, 17, 22, ...[...styles.getElementsByTagName("numFmt")]
    .filter((f) => /[dmy]/i.test(f.getAttribute("formatCode") || "") && !/[hs]/i.test(f.getAttribute("formatCode") || ""))
    .map((f) => Number(f.getAttribute("numFmtId")))]);
  const cellXfs = styles.getElementsByTagName("cellXfs")[0];
  const xfDate = cellXfs ? [...cellXfs.getElementsByTagName("xf")].map((xf) => dateFmts.has(Number(xf.getAttribute("numFmtId")))) : [];

  const rows = [...xml(await text(files[sheetName])).getElementsByTagName("row")].map((row) => {
    const out = [];
    [...row.getElementsByTagName("c")].forEach((c) => {
      const type = c.getAttribute("t");
      const v = c.getElementsByTagName("v")[0]?.textContent ?? c.getElementsByTagName("t")[0]?.textContent ?? "";
      let value = v;
      if (type === "s") value = shared[Number(v)] ?? "";
      else if (!type && v !== "" && xfDate[Number(c.getAttribute("s") || 0)]) value = excelDate(v);
      out[colIndex(c.getAttribute("r"))] = String(value).trim();
    });
    return out;
  }).filter((r) => r.some((c) => c));
  const [header = [], ...body] = rows;
  const keys = header.map((h) => String(h || "").trim());
  return body.map((r) => Object.fromEntries(keys.map((k, i) => [k, r[i] ?? ""])));
}

export async function readSpreadsheetFile(file) {
  if (/\.csv$/i.test(file.name)) return parseCsv(await file.text());
  if (/\.xlsx$/i.test(file.name)) return readXlsx(await file.arrayBuffer());
  throw new Error("Choose an .xlsx or .csv file.");
}
